// Resources and files (AdminResources.dc.html), owner and admin: every product's library of items in
// the sections Adam names (decision 8). Pick a product, then rename, move, delete or add a section, and
// add, edit, move or remove an item: a file, an online tool, a link, text to copy or a Vimeo video.
// A file goes through sign-upload, so replacing it keeps the link and the old version. An item can be
// shown in other products too, opens when its rule says, can be yours for life and marked new, and
// shows how many of the people who can open it have. What was removed in the last 30 days can be put
// back. The board has no Tweaks; its states are these interactions.
//
// At laptop width the editor is the card beside the sections, as the board draws it; below 1180px
// the page is 1 column and the editor opens in a dialog, with the same fields and actions (Adam,
// 4 Oct, answer 5).
import { useEffect, useMemo, useRef, useState } from 'react'
import { PageHeader, Dialog, Loading, Problem, EmptyState, useAdmin, useLoad, productDot, adminPath } from '../ui/index.js'
import { Link, navigate, useQueryParam } from '../../lib/router.jsx'
import { useMedia } from '../../lib/media.js'
import { useNow } from '../../lib/clock.js'
import { useViewAs } from '../../lib/viewAs.jsx'
import { ukToIso } from '../ui/format.js'
import { RsIcon } from '../resources/Icons.jsx'
import { SectionCard } from '../resources/Section.jsx'
import { EditorFields, EditorCard } from '../resources/Editor.jsx'
import * as M from '../resources/model.js'
import * as D from '../resources/data.js'
import '../../styles/admin-resources.css'

const NARROW = '(max-width: 1180px)'
const DAY = 86400000

// The dot beside a product's name: the board's colours (the free guide's is the pale tile)
const dotOf = p => (p.access_kind === 'free' && p.type === 'resource_pack' ? 'var(--shell-tile-light)' : productDot(p))

export default function Resources() {
  const { api, me, products, productsStatus, reloadProducts } = useAdmin()
  const actor = me?.who?.person_id || null
  const narrow = useMedia(NARROW)
  const nowMs = useNow()
  const list = useMemo(() => M.resourceProducts(products).map(p => ({ ...p, dot: dotOf(p) })), [products])
  const wanted = useQueryParam('product')
  const product = list.find(p => p.slug === wanted) || list[0] || null

  const live = useLoad(() => D.loadLive(api), [api])
  const lib = useLoad(() => (product ? D.loadProduct(api, product.id) : null), [api, product?.id])
  // whether marking something new in this product puts it in the bell (a product that speaks: live,
  // moved in and not in shadow), for "Mark it new"'s words (hub.bell_will_ring, Adam, 5 Oct, stage 6's batch)
  const speaks = useLoad(() => (product ? api.bell_will_ring({ kind: 'new_resource', product: product.id }) : false), [api, product?.id])

  const [ed, setEd] = useState(null) // { mode, plId, draft }
  const [confirmId, setConfirmId] = useState(null)
  const [renaming, setRenaming] = useState(null) // { id, value }
  const [secMsg, setSecMsg] = useState(null) // { id, text }
  const [deleting, setDeleting] = useState(null) // a section id, asked about
  const [newSection, setNewSection] = useState('')
  const [notice, setNotice] = useState(null) // { text, tone }
  const [busy, setBusy] = useState(false)
  const editorRef = useRef(null)

  // a new product starts clean
  useEffect(() => {
    setEd(null); setConfirmId(null); setRenaming(null); setSecMsg(null); setDeleting(null); setNotice(null)
  }, [product?.id])

  // the editor comes into view when it opens beside the sections
  const edKey = ed ? `${ed.mode}:${ed.plId || ed.draft.sectionId}` : null
  useEffect(() => {
    if (edKey && !narrow) editorRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' })
  }, [edKey, narrow])

  const data = lib.data
  const view = useMemo(() => (data && product ? buildView({ data, product, live: live.data || [], list, nowMs }) : null),
    [data, product, live.data, list, nowMs])

  const counts = useMemo(() => {
    const out = {}
    for (const pl of live.data || []) out[pl.product_id] = (out[pl.product_id] || 0) + 1
    return out
  }, [live.data])

  async function act(fn, done) {
    if (busy) return false
    setBusy(true)
    try {
      await fn()
      if (done) setNotice({ text: done, tone: 'done' })
      return true
    } catch (e) {
      setNotice({ text: api.words(e), tone: 'problem' })
      return false
    } finally {
      await Promise.all([lib.reload(), live.reload()])
      setBusy(false)
    }
  }

  function pickProduct(p) {
    if (p.id === product?.id) return
    navigate(`/admin/resources?product=${encodeURIComponent(p.slug)}`, { replace: true })
  }

  if (productsStatus === 'loading' && !products.length) return <><Head /><Loading /></>
  if (productsStatus === 'error' && !products.length) return <><Head /><Problem onRetry={reloadProducts} /></>
  if (!product) {
    return <><Head /><Note /><EmptyState>No product in the hub holds resources yet.</EmptyState></>
  }

  const editing = ed && view ? editorContext(ed, view) : null
  // closing the editor clears a line about the draft that was in it
  const closeEditor = () => { setEd(null); setNotice(n => (n?.tone === 'problem' ? null : n)) }

  async function save() {
    if (!editing) return
    const { draft } = ed
    const problem = M.draftProblem(draft, { mode: ed.mode, hasFile: Boolean(editing.fileName) })
    if (problem) { setNotice({ text: problem, tone: 'problem' }); return }
    const nowIso = new Date(nowMs).toISOString()
    const ctx = {
      actor, toIso: day => ukToIso(day, '00:00'), nowIso, newUntil: new Date(nowMs + 14 * DAY).toISOString(),
      sectionItems: view.live.filter(pl => pl.section_id === draft.sectionId && pl.id !== ed.plId),
      others: { sectionName: view.sections.find(s => s.id === draft.sectionId)?.name || '' },
    }
    const title = draft.title.trim()
    let line = null
    const ok = await act(async () => {
      // marked new: whether the database will put it in the bell, asked before the write (never for 1
      // already marked new, which this save leaves as it is)
      const rings = draft.isNew
        ? await api.bell_will_ring({ kind: 'new_resource', product: product.id, item: ed.mode === 'edit' ? editing.pl.id : null })
        : false
      if (ed.mode === 'add') {
        const pl = await D.addItem(api, { product, draft, ...ctx })
        line = M.savedLine('add', title, pl, view, { marked: rings })
      } else {
        const { pl, item, body, alsoIn, elsewhere } = editing
        await D.saveItem(api, { product, draft, pl, item, body, alsoBefore: alsoIn.map(p => p.id), elsewhere, ...ctx })
        line = M.savedLine('edit', title, M.releaseUnchanged(draft, pl) ? pl : { ...pl, ...M.releaseColumns(draft, ctx.toIso) }, view, { marked: rings })
      }
    })
    if (ok) { setEd(null); setNotice({ text: line, tone: 'done' }) }
  }

  const editorFooter = (
    <>
      <button type="button" className="ar-btn ar-btn--ink ar-btn--save" onClick={save} disabled={busy} aria-busy={busy || undefined}>
        {ed?.mode === 'add' ? 'Add it' : 'Save changes'}
      </button>
      <button type="button" className="ar-btn ar-btn--line ar-btn--cancel" onClick={closeEditor} disabled={busy}>Cancel</button>
    </>
  )
  const fields = editing && (
    <EditorFields mode={ed.mode} draft={ed.draft} setDraft={up => { setNotice(null); setEd(e => (e ? { ...e, draft: up(e.draft) } : e)) }}
      sections={view.sections} weeks={view.weeks} others={list.filter(p => p.id !== product.id)} fileName={editing.fileName}
      bell={speaks.data === true} />
  )
  const heading = editing ? (ed.mode === 'add' ? `Add to ${ed.openedIn || editing.sectionName}` : `Edit ${ed.draft.title.trim() || 'item'}`) : ''
  const flash = notice && (
    <div className={`ar-flash${notice.tone === 'problem' ? ' ar-flash--problem' : ''}`} role={notice.tone === 'problem' ? 'alert' : 'status'}>
      {notice.text}
    </div>
  )

  return (
    <>
      <Head />
      <Note />

      <div className="ar-products" role="group" aria-label="Product">
        {list.map(p => (
          <button key={p.id} type="button" className="ar-product" aria-pressed={p.id === product.id} onClick={() => pickProduct(p)}>
            <span className="ar-dot ar-dot--ring" style={{ background: p.dot }} aria-hidden="true" />
            {M.chipName(p)}
            <span className="ar-product-count">{live.data ? counts[p.id] || 0 : ''}</span>
          </button>
        ))}
      </div>

      {lib.status === 'error' && !data && <Problem error={lib.error} onRetry={lib.reload} />}
      {!view && lib.status !== 'error' && <Loading />}
      {view && (
        <div className="ar-grid">
          <div className="ar-sections">
            {narrow && flash && <div className="ar-flash-wrap">{flash}</div>}
            {view.sections.map((s, si) => (
              <SectionCard key={s.id} section={s} index={si} count={view.sections.length} busy={busy}
                rows={view.rowsBySection[s.id] || []} removed={view.removedBySection[s.id] || []}
                renaming={renaming?.id === s.id} renameValue={renaming?.value || ''}
                message={secMsg?.id === s.id ? secMsg.text : null} confirmDelete={deleting === s.id}
                editingId={ed?.mode === 'edit' ? ed.plId : null} confirmId={confirmId}
                onMove={dir => act(() => D.writeOrder(api, 'resource_sections', M.reorderWrites(view.sections, si, dir)))}
                onRename={() => { setRenaming({ id: s.id, value: s.name }); setSecMsg(null) }}
                onRenameValue={value => setRenaming(r => ({ ...r, value }))}
                onCancelRename={() => setRenaming(null)}
                onSaveRename={async () => {
                  const name = (renaming?.value || '').trim()
                  if (!name) return
                  if (name === s.name) { setRenaming(null); return }
                  if (await act(() => D.renameSection(api, { id: s.id, name }), 'Section renamed. Members see the new name straight away.')) setRenaming(null)
                }}
                onDelete={() => {
                  const rows = view.rowsBySection[s.id] || []
                  if (rows.length) {
                    setSecMsg({ id: s.id, text: `Move or remove its ${M.itemsWord(rows.length)} first. A section is only deleted when it's empty.` })
                    return
                  }
                  setSecMsg(null)
                  if ((view.removedBySection[s.id] || []).length) { setDeleting(s.id); return }
                  act(() => D.deleteSection(api, s.id), 'Section deleted.').then(ok => { if (ok && ed?.draft.sectionId === s.id) setEd(null) })
                }}
                onConfirmDelete={() => act(() => D.deleteSection(api, s.id), 'Section deleted.').then(() => setDeleting(null))}
                onKeepSection={() => setDeleting(null)}
                onAdd={() => {
                  setConfirmId(null); setNotice(null)
                  setEd({ mode: 'add', plId: null, openedIn: s.name, draft: M.blankDraft(s.id, view.weeks) })
                }}
                onMoveItem={(i, dir) => act(() => D.writeOrder(api, 'resource_placements', M.reorderWrites(view.rowsBySection[s.id].map(r => r.pl), i, dir)))}
                onEdit={plId => {
                  setConfirmId(null); setNotice(null)
                  setEd({ mode: 'edit', plId, draft: draftForEdit(plId, view, nowMs) })
                }}
                onAskRemove={plId => setConfirmId(c => (c === plId ? null : plId))}
                onKeep={() => setConfirmId(null)}
                onRemove={plId => {
                  const row = view.rowById[plId]
                  act(() => D.removePlacement(api, { id: plId, actor, at: new Date(nowMs).toISOString() }), `Removed ${row.title}.`)
                    .then(ok => { if (ok) { setConfirmId(null); if (ed?.plId === plId) setEd(null) } })
                }}
                onPutBack={plId => {
                  const r = view.removedById[plId]
                  act(() => D.putBack(api, { id: plId, title: r.title, productName: M.shortName(product) }),
                    `${r.title} is back. Members who have it see it now.`)
                }}
              />
            ))}

            <form className="ar-new-section" onSubmit={e => {
              e.preventDefault()
              const name = newSection.trim()
              if (!name) return
              act(() => D.addSection(api, { productId: product.id, name, sections: view.sections }),
                `${name} added. It stays hidden from members until something is in it.`).then(ok => { if (ok) setNewSection('') })
            }}>
              <input className="ar-input ar-input--section" aria-label="New section name" placeholder="New section, e.g. Bonuses"
                value={newSection} maxLength={60} onChange={e => setNewSection(e.target.value)} />
              <button type="submit" className="ar-btn ar-btn--ink ar-btn--section" disabled={busy}>Add section</button>
            </form>
          </div>

          <aside className="ar-aside" ref={editorRef}>
            {!narrow && flash}
            {(!editing || narrow) && (
              <section className="ar-card ar-closed" aria-labelledby="ar-closed-h">
                <h2 className="ar-closed-title" id="ar-closed-h">Add or edit</h2>
                <p className="ar-card-text">Press Add item on a section, or Edit on anything in it. Every item is 1 of 5 kinds: a file to download, an online tool, a link, text to copy, or a Vimeo video.</p>
              </section>
            )}
            {editing && !narrow && (
              <EditorCard heading={heading} onClose={closeEditor} footer={editorFooter}>{fields}</EditorCard>
            )}
            <section className="ar-card ar-how" aria-labelledby="ar-how-h">
              <h2 className="ar-how-title" id="ar-how-h">How members see it</h2>
              <p className="ar-card-text ar-card-text--sm">Each section becomes a tab on the product's Resources, in the product's own look and in this order. Anything not open yet shows with a lock and when it opens. Empty sections are hidden.</p>
              <p className="ar-card-text ar-card-text--sm">Opens are counted per member. Anything fewer than half have opened is flagged, so you can nudge them in the next session.</p>
              <SeeAsMember product={product} />
            </section>
          </aside>
        </div>
      )}

      {editing && narrow && (
        <Dialog title={heading} onClose={() => !busy && closeEditor()} busy={busy} footer={editorFooter}>
          <div className="ar-editor ar-editor--dialog">
            {fields}
            {notice?.tone === 'problem' && <p className="ar-problem" role="alert">{notice.text}</p>}
          </div>
        </Dialog>
      )}
    </>
  )
}

function Head() {
  return <PageHeader eyebrow="Downloads, tools, toolkits and bonuses" title="Resources and files" />
}

function Note() {
  return (
    <div className="ad-note ar-note" role="note">
      <RsIcon name="lock" size={18} />
      <span>Files are kept in private storage, and every download link expires after a few minutes. Recordings aren't stored here. They stay on Vimeo and are embedded, from <Link to="/admin/recordings">Recordings and dates</Link>.</span>
    </div>
  )
}

// "See it as a member": view as someone who has the product (a test login first), on its Resources.
// View as is the person page's (lib/viewAs.jsx): read only, logged, and it ends with Stop viewing.
function SeeAsMember({ product }) {
  const { api } = useAdmin()
  const viewAs = useViewAs()
  const [state, setState] = useState({ busy: false, line: '' })
  useEffect(() => { setState({ busy: false, line: '' }) }, [product.id])
  async function go() {
    setState({ busy: true, line: '' })
    try {
      const rows = await api.staff_people(product.access_kind === 'free'
        ? { role: 'member', status_group: 'active', include_test: true, limit: 20 }
        : { product: product.id, status_group: 'active', include_test: true, limit: 20 })
      const members = rows.filter(r => !r.role)
      const pick = members.find(r => r.is_test) || members[0]
      // Products' words, the same here (Adam, 5 Oct, question 17)
      if (!pick) { setState({ busy: false, line: "Nobody has it yet, so there's nobody to view as." }); return }
      const target = { id: pick.person_id, full_name: pick.full_name, role: pick.role || null }
      if (!viewAs.canViewAs(target)) { setState({ busy: false, line: api.WORDS.notAllowed }); return }
      // Stop viewing comes back here, to this product's Resources
      const ok = await viewAs.start(target, { to: M.memberPath(product), back: `${adminPath.resources()}?product=${product.slug}` })
      if (!ok) setState({ busy: false, line: '' })
    } catch (e) {
      setState({ busy: false, line: api.words(e) })
    }
  }
  return (
    <>
      <button type="button" className="ar-btn ar-btn--line ar-btn--see" onClick={go} disabled={state.busy || viewAs.busy}
        aria-busy={state.busy || viewAs.busy || undefined}>See it as a member</button>
      {(state.line || viewAs.error) && <p className="ar-problem" role="alert">{state.line || viewAs.error}</p>}
    </>
  )
}

// ---- What the page draws, from the rows ----------------------------------------------------------

function buildView({ data, product, live, list, nowMs }) {
  const items = Object.fromEntries((data.items || []).map(i => [i.id, i]))
  const bodies = Object.fromEntries((data.bodies || []).map(b => [b.item_id, b]))
  const counts = data.counts ? Object.fromEntries(data.counts.map(c => [c.placement_id, c])) : null
  const sections = M.sortByOrder(data.sections)
  const weeks = M.weeksOf(product, data.modules)
  const ctx = { modules: data.modules || [], sessions: data.sessions || [] }
  const livePl = M.sortByOrder((data.placements || []).filter(pl => !pl.removed_at))
  // the other products each item is in now, from every live placement in the hub
  const alsoOf = itemId => list.filter(p => p.id !== product.id && live.some(x => x.item_id === itemId && x.product_id === p.id))
  const elsewhereOf = (itemId, plId) => live.some(x => x.item_id === itemId && x.id !== plId)

  const rowsBySection = {}
  const rowById = {}
  for (const pl of livePl) {
    const item = items[pl.item_id]
    if (!item) continue
    const { fileName, updatedAt } = M.fileFacts(item, data.versions)
    const alsoIn = alsoOf(item.id)
    const row = {
      id: pl.id, pl, item, body: bodies[item.id] || null, kind: item.kind, title: M.titleOf(pl, item), fileName, alsoIn,
      elsewhere: elsewhereOf(item.id, pl.id),
      meta: M.metaLine({ pl, item, fileName, alsoIn, ...ctx }),
      isNew: Boolean(pl.new_until && Date.parse(pl.new_until) > nowMs),
      updated: updatedAt ? `Updated ${M.dayMonthOf(updatedAt)}` : null,
      opens: M.opensLine(counts?.[pl.id], { loaded: Boolean(counts), nowMs }),
    }
    ;(rowsBySection[pl.section_id] = rowsBySection[pl.section_id] || []).push(row)
    rowById[pl.id] = row
  }
  const removedBySection = {}
  const removedById = {}
  for (const pl of (data.placements || []).filter(x => M.recentlyRemoved(x, nowMs))
    .sort((a, b) => Date.parse(b.removed_at) - Date.parse(a.removed_at))) {
    const item = items[pl.item_id]
    if (!item) continue
    const r = { id: pl.id, title: M.titleOf(pl, item), removed_at: pl.removed_at }
    ;(removedBySection[pl.section_id] = removedBySection[pl.section_id] || []).push(r)
    removedById[pl.id] = r
  }
  return { sections, weeks, live: livePl, rowsBySection, rowById, removedBySection, removedById, ...ctx }
}

function editorContext(ed, view) {
  const sectionName = view.sections.find(s => s.id === ed.draft.sectionId)?.name || ''
  if (ed.mode === 'add') return { sectionName, fileName: null }
  const row = view.rowById[ed.plId]
  if (!row) return null
  return { sectionName, fileName: row.fileName, pl: row.pl, item: row.item, body: row.body, alsoIn: row.alsoIn, elsewhere: row.elsewhere }
}

function draftForEdit(plId, view, nowMs) {
  const row = view.rowById[plId]
  return M.draftFrom({ pl: row.pl, item: row.item, body: row.body, alsoIn: row.alsoIn, weeks: view.weeks, nowMs, sessions: view.sessions })
}
